import React, { useMemo, useState } from 'react'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { format, parseISO } from 'date-fns'
import { IAnomaliesV2SeriesPoint } from '../../shared/types'
import { varClass } from '../../shared'
import { formatInteger, robustYMax, tickFormatterFor } from '../shared'

const xTickFormatter = (s: string) => {
  try {
    return format(parseISO(s), 'MMM d')
  } catch {
    return s
  }
}

export const VolumeChart = (props: {
  series: IAnomaliesV2SeriesPoint[]
}) => {
  const { series } = props
  const [scale, setScale] = useState<'normal' | 'full'>('normal')

  const chartData = useMemo(
    () =>
      series.map((p) => ({
        date: p.eventDate,
        clicks: p.clicks ?? 0,
        conversions: p.conversions ?? 0,
      })),
    [series]
  )

  const clicksMax =
    scale === 'normal'
      ? robustYMax(chartData.map((r) => r.clicks))
      : null
  const convMax =
    scale === 'normal'
      ? robustYMax(chartData.map((r) => r.conversions))
      : null
  const isClipped =
    scale === 'normal' &&
    chartData.some(
      (r) =>
        (clicksMax != null && r.clicks > clicksMax) ||
        (convMax != null && r.conversions > convMax)
    )

  return (
    <div className="card anomalies-chart">
      <div className="card-body p-t-1">
        <div className="row row-space-between row-center m-b-3">
          <div className="text-dark text-bolder">Volume context</div>
          <div className="row row-center" style={{ gap: 12, flexWrap: 'wrap' }}>
            <div className="anomalies-chart-legend">
              <span className="anomalies-chart-legend-item">
                <span
                  className="anomalies-chart-legend-dot"
                  style={{ background: '#4F9EEC' }}
                />
                Clicks
              </span>
              <span className="anomalies-chart-legend-item">
                <span
                  className="anomalies-chart-legend-line"
                  style={{ borderTopColor: '#5DC97E' }}
                />
                Conversions
              </span>
            </div>
            <div className="row row-center" style={{ gap: 8 }}>
              <span className="text-light text-small">Scale:</span>
              <div className="anomalies-trend-toggle-group">
                <button
                  type="button"
                  className={varClass({
                    'anomalies-trend-toggle': true,
                    'active': scale === 'normal',
                  })}
                  onClick={() => setScale('normal')}
                >
                  Normal
                </button>
                <button
                  type="button"
                  className={varClass({
                    'anomalies-trend-toggle': true,
                    'active': scale === 'full',
                  })}
                  onClick={() => setScale('full')}
                >
                  Full
                </button>
              </div>
            </div>
          </div>
        </div>
        <ResponsiveContainer width="99.9%" height={220}>
          <ComposedChart
            data={chartData}
            margin={{ top: 8, left: 0, right: 0, bottom: 0 }}
          >
            <CartesianGrid
              horizontal
              vertical={false}
              strokeDasharray="6"
              stroke="#EDEFF2"
            />
            <Tooltip content={<VolumeTooltip />} />
            <Bar
              yAxisId="clicks"
              dataKey="clicks"
              fill="#4F9EEC"
              opacity={0.5}
              radius={[2, 2, 0, 0]}
            />
            <Line
              yAxisId="conversions"
              dataKey="conversions"
              stroke="#5DC97E"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
              tickFormatter={xTickFormatter}
              tick={{ fontSize: 11, fill: '#828B9E' }}
            />
            <YAxis
              yAxisId="clicks"
              axisLine={false}
              tickLine={false}
              width={48}
              tickFormatter={tickFormatterFor('number')}
              tick={{ fontSize: 11, fill: '#828B9E' }}
              domain={[0, clicksMax != null ? clicksMax * 1.05 : 'auto']}
              allowDataOverflow
            />
            <YAxis
              yAxisId="conversions"
              orientation="right"
              axisLine={false}
              tickLine={false}
              width={40}
              tickFormatter={tickFormatterFor('number')}
              tick={{ fontSize: 11, fill: '#828B9E' }}
              domain={[0, convMax != null ? convMax * 1.05 : 'auto']}
              allowDataOverflow
            />
          </ComposedChart>
        </ResponsiveContainer>
        {isClipped && (
          <div className="text-light text-small m-t-1 anomalies-chart-clip-note">
            Peak values clipped — switch to Full to see them.
          </div>
        )}
      </div>
    </div>
  )
}

const VolumeTooltip = (props: any) => {
  const { active, payload, label } = props
  if (!active || !payload || !payload.length) return null
  const row = payload[0]?.payload
  if (!row) return null
  return (
    <div className="anomalies-chart-tooltip">
      <div className="anomalies-chart-tooltip-date">
        {label ? format(parseISO(label), 'MMM d, yyyy') : ''}
      </div>
      <div className="anomalies-chart-tooltip-row">
        <span
          className="anomalies-chart-tooltip-dot"
          style={{ background: '#4F9EEC' }}
        />
        <span className="anomalies-chart-tooltip-label">Clicks</span>
        <span className="anomalies-chart-tooltip-value">
          {formatInteger(row.clicks)}
        </span>
      </div>
      <div className="anomalies-chart-tooltip-row">
        <span
          className="anomalies-chart-tooltip-dot"
          style={{ background: '#5DC97E' }}
        />
        <span className="anomalies-chart-tooltip-label">Conversions</span>
        <span className="anomalies-chart-tooltip-value">
          {formatInteger(row.conversions)}
        </span>
      </div>
    </div>
  )
}
